import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { sendMagicLink, signInWithPasskey } from "../lib/auth.js";
import { useSession } from "../lib/useSession.js";

const SUPPORTS_PASSKEY = typeof window !== "undefined" && !!window.PublicKeyCredential;

export default function Login() {
  const session = useSession();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [mode, setMode] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (session) nav("/portal", { replace: true });
  }, [session, nav]);

  // Show error from URL params (magic-link verify failed)
  useEffect(() => {
    const err = params.get("error");
    if (err === "expired") setError("Your sign-in link has expired. Request a new one below.");
    else if (err === "invalid_token") setError("That link isn't valid.");
    else if (err === "server_error") setError("Server error verifying the link. Try again.");
  }, [params]);

  function continueToFactor(e) {
    e.preventDefault();
    if (!email || !email.includes("@")) { setError("Enter a valid email."); return; }
    setError(null);
    setStep(2);
  }

  async function handlePasskey() {
    setError(null);
    setSubmitting(true);
    const res = await signInWithPasskey(email);
    setSubmitting(false);
    if (res.error) { setError(res.error); return; }
    if (res.mode === "demo") { nav("/portal"); return; }
    nav("/portal");
  }

  async function handleMagicLink() {
    setError(null);
    setSubmitting(true);
    const res = await sendMagicLink(email);
    setSubmitting(false);
    if (res.error) { setError(res.error); return; }
    if (res.mode === "demo") { nav("/portal"); return; }
    setMode(res.mode);
    setSubmitted(true);
  }

  // ------ render ------
  return (
    <section className="login-page">
      <div className="login-grid">

        {/* LEFT — brand + welcome */}
        <div className="login-side">
          <div className="login-side-inner">
            <Link to="/" className="brand" style={{ marginBottom: 36 }}>
              <img src="/df-logo.png" alt="" />
              <span>Daksfirst</span>
            </Link>
            <div className="eyebrow">Investor portal</div>
            <h1 style={{ color: "var(--white)", marginTop: 14, fontSize: "clamp(32px, 4vw, 44px)" }}>
              Welcome back.
            </h1>
            <p className="sub spacer-s" style={{ fontSize: 17 }}>
              Two-step sign-in: enter your email, then verify with a passkey
              (Touch ID / Face ID / hardware key) or an email sign-in link.
            </p>

            <div className="login-points spacer-l">
              <div className="login-point">
                <span className="lp-num">01</span>
                <div>
                  <div className="lp-title">Phishing-resistant by design</div>
                  <div className="lp-text">Passkeys bind cryptographically to invest.daksfirst.com — they cannot be reused on a fake site.</div>
                </div>
              </div>
              <div className="login-point">
                <span className="lp-num">02</span>
                <div>
                  <div className="lp-title">No passwords</div>
                  <div className="lp-text">Your passkey is stored on your device or hardware key. Nothing for an attacker to phish.</div>
                </div>
              </div>
              <div className="login-point">
                <span className="lp-num">03</span>
                <div>
                  <div className="lp-title">Email fallback</div>
                  <div className="lp-text">If your authenticator is unavailable, request a one-time sign-in link delivered to your registered email.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="login-form-side">
          <div className="login-form-inner">
            {submitted && mode === "magic-link" ? (
              <div>
                <div className="eyebrow">Check your email</div>
                <h2 style={{ color: "var(--white)", marginTop: 14, marginBottom: 18 }}>
                  Sign-in link sent.
                </h2>
                <p className="sub" style={{ fontSize: 16, marginBottom: 28 }}>
                  We&rsquo;ve sent a one-click sign-in link to <b style={{ color: "var(--white)" }}>{email}</b>.
                  The link expires in 15 minutes.
                </p>
                <p className="sub small" style={{ marginBottom: 28 }}>
                  Didn&rsquo;t receive anything? Check spam, or email{" "}
                  <a href="mailto:info@daksfirst.com" style={{ color: "var(--gold)" }}>info@daksfirst.com</a>.
                </p>
                <button className="btn btn-ghost" onClick={() => { setSubmitted(false); setMode(null); setStep(1); }}>
                  Use a different email
                </button>
              </div>
            ) : step === 1 ? (
              <form className="login-form" onSubmit={continueToFactor}>
                <div className="eyebrow">Sign in · Step 1 of 2</div>
                <h2 style={{ color: "var(--white)", marginTop: 14, marginBottom: 28, fontSize: 28 }}>
                  Investor sign-in.
                </h2>
                {error && <p className="login-error">{error}</p>}

                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@familyoffice.com"
                />
                <button type="submit" className="btn btn-primary"
                        style={{ width: "100%", justifyContent: "center", marginTop: 18 }}>
                  Continue →
                </button>

                <div className="login-divider"><span>or</span></div>
                <a href="mailto:info@daksfirst.com?subject=Investor%20portal%20access%20request"
                   className="btn btn-ghost"
                   style={{ width: "100%", justifyContent: "center" }}>
                  Request access
                </a>

                <p className="login-foot">
                  Investor portal access is restricted to professional and institutional
                  investors. By signing in you confirm you meet that classification under
                  applicable regulations.
                </p>
              </form>
            ) : (
              <div className="login-form">
                <div className="eyebrow">Sign in · Step 2 of 2</div>
                <h2 style={{ color: "var(--white)", marginTop: 14, marginBottom: 8, fontSize: 28 }}>
                  Verify your identity
                </h2>
                <p className="sub" style={{ fontSize: 14, marginBottom: 24 }}>
                  Signing in as <b style={{ color: "var(--white)" }}>{email}</b>
                </p>
                {error && <p className="login-error">{error}</p>}

                {SUPPORTS_PASSKEY && (
                  <button onClick={handlePasskey} disabled={submitting}
                          className="btn btn-primary"
                          style={{ width: "100%", justifyContent: "center", marginBottom: 12 }}>
                    {submitting ? "Verifying…" : "Sign in with passkey"}
                  </button>
                )}
                <button onClick={handleMagicLink} disabled={submitting}
                        className="btn btn-ghost"
                        style={{ width: "100%", justifyContent: "center" }}>
                  {submitting ? "Sending…" : "Email me a sign-in link"}
                </button>

                <button onClick={() => { setStep(1); setError(null); }}
                        className="login-back" style={{ marginTop: 20 }}>
                  ← Use a different email
                </button>

                <p className="login-foot spacer-m">
                  Passkeys use Face ID, Touch ID, Windows Hello, or a hardware security key
                  on this device. Never typed, never phished.
                </p>
              </div>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}
